export type EventCtaTreatment = "ticket" | "info" | "go" | "note" | "none";

export interface EventCta {
  treatment: EventCtaTreatment;
  label: string;
  url?: string;
  accessibilityLabel: string;
}

type CostKind = "paid" | "free" | "donation" | "rsvp" | "unknown";

function classifyCost(price: string | null | undefined): {
  kind: CostKind;
  amount?: string;
} {
  const raw = price?.trim();
  if (!raw) return { kind: "unknown" };
  const lower = raw.toLowerCase();

  if (/donation|donate|pay what you can|pwyc|sliding scale/.test(lower)) {
    return { kind: "donation" };
  }
  if (lower === "free") return { kind: "free" };
  if (/\d/.test(raw) || raw.includes("$")) return { kind: "paid", amount: raw };
  if (/\brsvp\b|registration required|\bregister\b/.test(lower)) {
    return { kind: "rsvp" };
  }
  if (/\bfree\b/.test(lower)) return { kind: "free" };
  return { kind: "unknown", amount: raw };
}

const TICKETING_HOSTS = [
  "dice.fm",
  "ra.co",
  "eventbrite.com",
  "humanitix.com",
  "partiful.com",
  "lu.ma",
  "luma.events",
  "meetup.com",
  "ticketmaster.com",
  "seatgeek.com",
  "stubhub.com",
  "songkick.com",
  "tickettailor.com",
  "bandsintown.com",
];

function urlLooksLikeRsvpOrTicketing(url: string): boolean {
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return false;
  }
  return TICKETING_HOSTS.some((d) => host === d || host.endsWith(`.${d}`));
}

export function deriveEventCta(input: {
  price?: string | null;
  ticketUrl?: string | null;
}): EventCta {
  const url = input.ticketUrl?.trim() || undefined;
  const hasUrl = !!url;
  const { kind, amount } = classifyCost(input.price);

  switch (kind) {
    case "paid": {
      const cost = amount ?? "Paid";
      if (!hasUrl) {
        return {
          treatment: "note",
          label: cost,
          accessibilityLabel: `Price: ${cost}`,
        };
      }
      if (urlLooksLikeRsvpOrTicketing(url)) {
        return {
          treatment: "ticket",
          label: `${cost} · Get tickets`,
          url,
          accessibilityLabel: `Get tickets, ${cost}`,
        };
      }
      return {
        treatment: "info",
        label: `${cost} · More info`,
        url,
        accessibilityLabel: `${cost}, more info`,
      };
    }
    case "free":
      if (!hasUrl) {
        return {
          treatment: "go",
          label: "Free · No RSVP needed",
          accessibilityLabel: "Free, no RSVP needed",
        };
      }
      if (urlLooksLikeRsvpOrTicketing(url)) {
        return {
          treatment: "ticket",
          label: "Free · RSVP",
          url,
          accessibilityLabel: "Free, RSVP to attend",
        };
      }
      return {
        treatment: "info",
        label: "Free · More info",
        url,
        accessibilityLabel: "Free, more info",
      };
    case "donation":
      if (!hasUrl) {
        return {
          treatment: "note",
          label: "Donation · Pay what you can",
          accessibilityLabel: "Donation based, pay what you can",
        };
      }
      if (urlLooksLikeRsvpOrTicketing(url)) {
        return {
          treatment: "ticket",
          label: "Donation · RSVP",
          url,
          accessibilityLabel: "Donation based, RSVP to attend",
        };
      }
      return {
        treatment: "info",
        label: "Donation · More info",
        url,
        accessibilityLabel: "Donation based, more info",
      };
    case "rsvp":
      return hasUrl
        ? {
            treatment: "ticket",
            label: "RSVP required",
            url,
            accessibilityLabel: "RSVP required",
          }
        : {
            treatment: "note",
            label: "RSVP required",
            accessibilityLabel: "RSVP required",
          };
    case "unknown":
    default:
      if (hasUrl) {
        return {
          treatment: "info",
          label: amount ? `${amount} · More info` : "More info",
          url,
          accessibilityLabel: amount ? `${amount}, more info` : "More info",
        };
      }
      if (amount) {
        return {
          treatment: "note",
          label: amount,
          accessibilityLabel: amount,
        };
      }
      return {
        treatment: "none",
        label: "",
        accessibilityLabel: "",
      };
  }
}
